// @dynamic-i18n-keys
//   packagePicker.coreOffer.cta.gift
//   packagePicker.coreOffer.currentPlan
//   packagePicker.coreOffer.cta.extend
//   packagePicker.coreOffer.cta.upgradeNow
//   packagePicker.coreOffer.cta.upgrade
//   packagePicker.coreOffer.core_access.title
//   packagePicker.coreOffer.core_plus.title
//   packagePicker.coreOffer.core_pro.title
//   packagePicker.coreOffer.core_pro_plus.title
//   packagePicker.coreOffer.core_max.title
import type { FC } from 'react';
import { useContext, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import { BiData, PackagePickerClickBiEvent } from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import UserSymbol from '@wix/da-shared-react/pkg/User/_partials/UserSymbol';
import { PapiProductV3, PapiSubscriptionV3 } from '@wix/da-papi-types';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BillingCyclePopup from '../Popup/BillingCyclePopup';
import { addSubproductIdToURL } from '../../../helpers/url';
import { CoreTypes } from '../types';
import { type PackagePickerStoreSlice } from '../../../redux/packagePicker/types';
import {
  getPriceBeforeDiscountForPackagePickerV3,
  getPriceForPackagePickerV3,
  getRecommendedPackageType,
} from '../utils';
import { packagesData } from '../constants';
import useOpenCPPopupOnIdParam from '../hooks/useOpenCPPopupOnIdParam';

import s from './CorePrice.scss';

export interface Props {
  coreType: CoreTypes;
  packageProducts: PapiProductV3[];
  pickerMode: PackagePickerStoreSlice['pickerMode'];
  packageConfig?: PackagePickerStoreSlice['packageConfig'];
  alreadyHasSubscription: boolean;
  currentCoreSubscription?: PapiSubscriptionV3;
  highlighted?: boolean;
  sale: PackagePickerStoreSlice['sale'];
}

const CorePrice: FC<Props> = ({
  coreType,
  packageProducts,
  pickerMode,
  packageConfig,
  alreadyHasSubscription,
  currentCoreSubscription,
  sale,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const [selectedPackage, setSelectedPackage] = useState<number[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openBilling = () => setIsPopupOpen(true);

  const monthlyPackageProduct = packageProducts.find(
    p => p.corePackage?.coreType === coreType && p.corePackage?.interval === 'month'
  ) as PapiProductV3;
  const yearlyPackageProduct = packageProducts.find(
    p => p.corePackage?.coreType === coreType && p.corePackage?.interval === 'year'
  ) as PapiProductV3;

  const isGift = pickerMode === 'gift';
  const isCurrent = currentCoreSubscription?.orderV3?.product.corePackage?.coreType === coreType;
  const isPackageDisabled =
    packageConfig?.[camelCase(coreType)]?.enabled === false ||
    (monthlyPackageProduct.corePackage?.status === 'disabled' &&
      yearlyPackageProduct.corePackage?.status === 'disabled');

  const isDisabled = isPackageDisabled;

  // Discount might apply to monthly or yearly packages only, or both.
  const hasDiscount = Boolean(
    (yearlyPackageProduct.discount || monthlyPackageProduct.discount) && sale?.ppShowDiscount
  );

  useOpenCPPopupOnIdParam({
    monthlyPackageProduct,
    yearlyPackageProduct,
    setSelectedPackage,
    setIsPopupOpen,
  });

  const handleSelect = selected => {
    setSelectedPackage(selected);
    openBilling();

    addSubproductIdToURL(selected[0]);
  };

  let biButtonType = 'gift';
  if (pickerMode === 'buy') {
    biButtonType = 'select';
  }

  const biCoreType = coreType === CoreTypes.CORE_ACCESS ? 'core' : coreType;

  const isRecommended = getRecommendedPackageType(packageConfig) === coreType;

  const showCTA = isGift || !currentCoreSubscription || currentCoreSubscription.isUpgradable;

  const getButtonText = () => {
    if (isGift) {
      return 'packagePicker.coreOffer.cta.gift';
    }
    if (isCurrent) {
      return isPackageDisabled
        ? 'packagePicker.coreOffer.currentPlan'
        : 'packagePicker.coreOffer.cta.extend';
    }
    return isRecommended
      ? 'packagePicker.coreOffer.cta.upgradeNow'
      : 'packagePicker.coreOffer.cta.upgrade';
  };

  const hasBrandedButton = isMobile || isRecommended || (currentCoreSubscription && !isDisabled);
  const showCurrentPlanTag = isCurrent && !isGift;

  const renderGhostButton = () => {
    const ghostButton = (
      <GhostButton
        className={s['button']}
        size={'large'}
        variant={'core-orange'}
        onClick={() =>
          handleSelect([monthlyPackageProduct.subproductId, yearlyPackageProduct.subproductId])
        }
        disabled={isDisabled}
        biData={BiData<PackagePickerClickBiEvent>({
          evid: 501,
          button_type: 'select',
          core_type: biCoreType,
          give_core_or_become_core: alreadyHasSubscription
            ? 'upgrade_core'
            : isGift
              ? 'give_core'
              : 'become_core',
        })}
      >
        {t(getButtonText())}
      </GhostButton>
    );

    return isDisabled ? (
      <SimpleTooltip
        placement="bottom"
        text={t('packagePicker.coreOffer.disabled.tooltip')}
        usePortal
        offsetModifier={[0, 8]}
      >
        {ghostButton}
      </SimpleTooltip>
    ) : (
      ghostButton
    );
  };

  return (
    <div
      className={cn(
        s['root'],
        isRecommended && s['recommended'],
        hasDiscount && s['with-discount']
      )}
    >
      {hasDiscount && (
        <div className={s['sale-badge']}>
          {t('packagePicker.coreOffer.saleBadge', {
            discountPercent: yearlyPackageProduct.discount
              ? yearlyPackageProduct.discount?.amount.percent
              : monthlyPackageProduct.discount?.amount.percent,
          })}
        </div>
      )}
      {showCurrentPlanTag && (
        <div className={cn(s['tag'], s['current-plan'])}>
          {t('packagePicker.coreOffer.currentPlan')}
        </div>
      )}
      {isRecommended && !showCurrentPlanTag && (
        <div className={s['tag']}>{t('packagePicker.coreOffer.popular')}</div>
      )}
      <div className={s['core-logo']}>
        <UserSymbol userSymbol={coreType} className={s['core-symbol']} />
      </div>
      <div className={s['title']}>{t(packagesData[coreType].title)}</div>
      {!isMobile && <div className={s['divider']} />}
      <div className={s['price-wrapper']}>
        <div className={s['price']}>
          {formatDollars(
            getPriceForPackagePickerV3({
              monthlyPackageProduct,
              yearlyPackageProduct,
              showDiscount: Boolean(sale?.ppShowDiscount),
            })
          )}
        </div>
        <div className={s['period']}>{t('packagePicker.coreOffer.perMonth')}</div>
      </div>
      {hasDiscount && (
        <div className={s['price-before']}>
          {formatDollars(
            getPriceBeforeDiscountForPackagePickerV3({
              monthlyPackageProduct,
              yearlyPackageProduct,
            })
          )}
        </div>
      )}
      {showCTA && hasBrandedButton && (
        <BrandedButton
          className={cn(s['button'], s['branded-button'])}
          size={'large'}
          variant={'core-orange'}
          onClick={() =>
            handleSelect([monthlyPackageProduct.subproductId, yearlyPackageProduct.subproductId])
          }
          disabled={isDisabled}
          biData={BiData<PackagePickerClickBiEvent>({
            evid: 501,
            button_type: biButtonType as any,
            core_type: biCoreType,
            give_core_or_become_core: alreadyHasSubscription
              ? 'upgrade_core'
              : isGift
                ? 'give_core'
                : 'become_core',
          })}
        >
          {t(getButtonText())}
        </BrandedButton>
      )}
      {showCTA && !hasBrandedButton && renderGhostButton()}

      <BillingCyclePopup
        selectedPackage={selectedPackage}
        coreType={coreType}
        isOpen={isPopupOpen}
        onRequestClose={() => setIsPopupOpen(false)}
        withCloseButton
      />
    </div>
  );
};
CorePrice.displayName = 'CorePrice';

export default CorePrice;
